import { dateOnlyOf } from '@/lib/utils/custom-date/custom-date';
import { DatedWorkDay, workDaysOn } from '@/lib/utils/work-days/work-days';

/** The parts of a leave request its days are counted from. */
export interface CountedLeave {
  /** Date only. */
  StartDate: string;
  /** Date only; a one-day request may leave it out. */
  EndDate?: string;
  LeaveType?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_DEFAULT = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

/** Calendar-day arithmetic on plain "YYYY-MM-DD" days (date only, never zone-converted). */
const toMs = (day: string) => {
  const [year, month, date] = day.split('-').map(Number);
  return Date.UTC(year, month - 1, date);
};

/** The holidays' days ("YYYY-MM-DD"), to count leave days against. */
export const holidayDaysOf = (holidays: readonly { StartDate: string }[]): Set<string> =>
  new Set(holidays.map((holiday) => dateOnlyOf(holiday.StartDate)));

/** Working days of `from`..`to`: not a holiday, a weekday of the work days in force (Monday to Friday
 * without any), like the workflow's `leaveTakeOf`; a half day is 0.5. */
export const workingDaysOf = (
  from: string,
  to: string,
  isHalfDay: boolean,
  workDays: readonly DatedWorkDay[],
  holidays: ReadonlySet<string>
) => {
  let days = 0;
  for (let ms = toMs(from), end = toMs(to); ms <= end; ms += DAY_MS) {
    const day = new Date(ms).toISOString().slice(0, 10);
    if (holidays.has(day)) continue;
    const inForce = workDaysOn(workDays, day).map((row) => row.WorkDay);
    const names = inForce.length > 0 ? inForce : WEEKDAY_DEFAULT;
    if (names.includes(WEEKDAYS[new Date(ms).getUTCDay()])) days += 1;
  }
  return isHalfDay && days > 0 ? 0.5 : days;
};

/**
 * The days a Pending or Approved request takes, counted now: its working days (`workingDaysOf` with the
 * HR term's work days, the workflow's `leaveDaysOf`). A request stores no day count, so a change of the work
 * days or the holidays after it was made counts at once. With `window` ("YYYY-MM-DD", both inclusive) only its
 * days inside count. `holidays` must cover the days counted.
 */
export const leaveDaysOf = (
  leave: CountedLeave,
  workDays: readonly DatedWorkDay[],
  holidays: ReadonlySet<string>,
  window?: { start: string; end: string }
) => {
  const start = dateOnlyOf(leave.StartDate);
  const end = dateOnlyOf(leave.EndDate) || start;
  const from = window && window.start > start ? window.start : start;
  const to = window && window.end < end ? window.end : end;
  if (!from || !to || from > to) return 0;
  return workingDaysOf(from, to, leave.LeaveType === 'HalfDay', workDays, holidays);
};
